<!-- TEMPLATE -->
<template>
  <div class="perspectives-summary">
    <div class='row'>
      <div class='small-12 medium-12 large-12 hd-12 columns dashboard-content'>
        <dean-section-title content='true'>
          <span slot='title'>
              <span class='avenir-heavy'>Queue Monitor</span>
          </span>
        </dean-section-title>

        <dean-notification>
        </dean-notification>

        <div class="filter">
          <dean-form-row
              @input="update(true)"
              v-model="pageMeta.query"
              class="top-bar"
              :hide-label="true"
          >Filter</dean-form-row>

          <dean-form-row
              @input="update(true)"
              v-model="pageMeta.queueName"
              class="top-bar"
              :hide-label="true"
          >Queue name</dean-form-row>

          <button :class="{active: pageMeta.jobRunning == true}" id="jobRunning">
            <dean-icon :glyph="clockIcon"
                       @click.native="pageMeta.jobRunning = !pageMeta.jobRunning; update()"
                       width="20"
                       height="20"
            ></dean-icon>
            <b-tooltip target="jobRunning" placement="bottom">
              Running
            </b-tooltip>
          </button>

          <button :class="{active: pageMeta.jobSucceeded == true}" id="jobSucceeded">
            <dean-icon :glyph="checkmarkIcon"
                       @click.native="pageMeta.jobSucceeded = !pageMeta.jobSucceeded; update()"
                       width="20"
                       height="20"
            ></dean-icon>
            <b-tooltip target="jobSucceeded" placement="bottom">
              Succeeded
            </b-tooltip>
          </button>

          <button :class="{active: pageMeta.jobFailed == true}" id="jobFailed">
            <dean-icon :glyph="deleteIcon"
                       @click.native="pageMeta.jobFailed = !pageMeta.jobFailed; update()"
                       width="20"
                       height="20"
            ></dean-icon>
            <b-tooltip target="jobFailed" placement="bottom">
              Failed
            </b-tooltip>
          </button>

          <button :class="{active: pageMeta.jobStale == true}" id="jobStale">
            <dean-icon :glyph="warningIcon"
                       @click.native="pageMeta.jobStale = !pageMeta.jobStale; update()"
                       width="20"
                       height="20"
            ></dean-icon>
            <b-tooltip target="jobStale" placement="bottom">
              Stale
            </b-tooltip>
          </button>

          <button :class="{active: pageMeta.jobQueued == true}" id="jobQueued">
            <dean-icon :glyph="calendarTimeIcon"
                       @click.native="pageMeta.jobQueued = !pageMeta.jobQueued; update()"
                       width="20"
                       height="20"
            ></dean-icon>
            <b-tooltip target="jobQueued" placement="bottom">
              Queued
            </b-tooltip>
          </button>
        </div>

        <dean-table
          :data='queueMonitors'
          :columns="columns"
          @column-click='columnClick'
          @cell-click="cellClick"
          :hover="true">
        </dean-table>

        <dean-pagination
            :current-page='metaData.pageNumber'
            :total-items='metaData.totalResults'
            :items-per-page='metaData.itemsPerPage'
            :select-items-per-page='selectItemsPerPage'
            :visible-pages='metaData.visiblePages'
            @page-changed='paginationChanged'
            @items-per-page='itemsPerPageChanged'
        >
        </dean-pagination>
      </div>
    </div>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
  import { Component } from 'vue-property-decorator';
  import axios from 'axios';
  import deanTableMixin from '@/mixins/deanTableMixin';
  import sideMenuAdminMixin from '@/mixins/sideMenuAdminMixin';
  import {mixins} from 'vue-class-component';

  import {
    // SVG
    calendarTimeIcon,
    checkmarkIcon,
    warningIcon,
    deleteIcon,
    clockIcon,

    // COMPONENTS
    deanSectionTitle,
    deanSectionTitleItem,
    deanTable,
    deanPagination,
  } from '@deanone/dean-components';
  import DeanIcon from '@/components/deanComponents/deanIcon.vue';
  import deanFormRow from "@/components/deanComponents/deanFormRow.vue";
  import {BTooltip} from 'bootstrap-vue';
  import deanNotification from "@/components/deanComponents/deanNotification.vue";
  import {deanDialogBus} from "@/main";
  import {QueueJobStatusEnum} from "@/enums/QueueJobStatusEnum";

  interface QueueMonitor {
    id: number;
    jobUuid: string;
    queue: string;
    name: string;
    attempt: number;
    status: string;
    startedAt: string;
    finishedAt: string;
  }

  @Component({
    components: {
      deanPagination,
      deanNotification,
      deanFormRow,
      DeanIcon,
      deanSectionTitle,
      deanSectionTitleItem,
      deanTable,
      BTooltip,
    },
  })

  export default class List extends mixins(deanTableMixin, sideMenuAdminMixin) {
    public sideMenu: object = {
      title: {
        text: 'Queue Monitor',
        mobile: {
          close: {
            text: 'Close',
          },
        },
      },
      filters: {},
    };

    public clockIcon: object = clockIcon;
    public calendarTimeIcon: object = calendarTimeIcon;
    public checkmarkIcon: object = checkmarkIcon;
    public warningIcon: object = warningIcon;
    public deleteIcon: object = deleteIcon;

    public queryTimeout!: number;

    // DATA

    private queueMonitors: Array<QueueMonitor> = [];

    public columns: any = {
      id: {
        content: 'ID',
        align: 'center',
        sort: true,
        dql: 'id',
      },
      jobUuid: {
        content: 'Job UUID',
        align: 'left',
        sort: true,
        dql: 'jobUuid',
      },
      queue: {
        content: 'Queue name',
        align: 'left',
        sort: true,
        dql: 'queue',
      },
      name: {
        content: 'Job name',
        align: 'left',
        sort: true,
        dql: 'name',
      },
      attempt: {
        content: 'Attempt',
        align: 'center',
        sort: false,
        dql: 'attempt',
      },
      status: {
        content: 'Status',
        align: 'center',
        sort: true,
        dql: 'status',
      },
      startedAt: {
        content: 'Started At',
        align: 'center',
        sort: true,
        dql: 'startedAt',
      },
      finishedAt: {
        content: 'Finished At',
        align: 'center',
        sort: true,
        dql: 'finishedAt',
      },
    };

    // METHODS
    get metaData() {
      return this.pageMeta;
    }

    public loadList(meta: any) {
      this.$store.dispatch('setLoadOverlay', true);

      axios.post('/-/trade/api/admin.queueMonitor.list', {
        meta: {
          startIndex: ((meta.pageNumber - 1) * meta.itemsPerPage),
          numberOfItems: meta.itemsPerPage,
        },
        sort: meta.sort,
        filter: meta.query,
        queueName: meta.queueName,
        statuses: [
          ...(meta.jobRunning ? [QueueJobStatusEnum.RUNNING] : []),
          ...(meta.jobSucceeded ? [QueueJobStatusEnum.SUCCEEDED] : []),
          ...(meta.jobFailed ? [QueueJobStatusEnum.FAILED] : []),
          ...(meta.jobStale ? [QueueJobStatusEnum.STALE] : []),
          ...(meta.jobQueued ? [QueueJobStatusEnum.QUEUED] : []),
        ],
      })
      .then(
        (response: any) => {
          this.queueMonitors = this.queueMonitorsFactory(response.data.data);

          this.updateMeta(response.data.meta);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    private queueMonitorsFactory(items: object): Array<QueueMonitor> {
      let queueMonitors: Array<QueueMonitor> = [];

      Object.values(items).forEach((item: any) => {
        let queueMonitor: QueueMonitor = {
          id: item.id,
          jobUuid: item.jobUuid,
          queue: item.queue,
          name: item.basename,
          attempt: item.attempt,
          status: item.status?.name,
          startedAt: item.startedAt,
          finishedAt: item.finishedAt,
        };

        queueMonitors.push(queueMonitor);
      })

      return queueMonitors;
    }

    public cellClick(target: {
      cell: object, row: QueueMonitor, column: string,
    }) {
      this.$router.push({
        name: 'standingData.queueMonitor.info',
        params: {id: target.row.id.toString()},
      });
    }

    public created() {
      this.keepMetaSavedInState();
      this.setDefaultSort('-id');
      this.loadList(this.pageMeta);
      this.$store.dispatch('setStandingDataSideMenu', this.sideMenu);
    }

    public mounted() {
      deanDialogBus.$on('saved', (payload: any) => {
        this.loadList(this.pageMeta);
      });
    }

    public destroyed() {
      deanDialogBus.$off('saved');
    }

    public update(timeout: boolean|null = null) {
      this.pageMeta.pageNumber = 1;

      const self = this;
      const meta: any = self.pageMeta;

      if (timeout) {
        if (this.queryTimeout) {
          clearTimeout(this.queryTimeout);
        }
        this.queryTimeout = setTimeout(() => {
          self.loadList(meta);
        }, 800);
      } else {
        this.loadList(meta);
      }
    }
  }
</script>
<!-- STYLE -->
<style lang="scss" scoped>

@import '~@deanone/dean-components/src/scss/vars';

.filter {
  display: flex;
}

button {
  fill: var(--color-dark, $color-dark);
  background: none;
  padding: 5px;
  border: 0;
  margin: 5px;
  transition: fill .3s;
  border-radius: 5px;
}

button.active,
button:hover {
  fill: var(--color-secondary, $color-secondary);
}

</style>